<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Players</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Newly Registered Players</h2>
    <v-card class="shadow">
      <div class="d-flex justify-space-between align-center bs-py-3 bs-px-4">
        <div class="d-flex">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                @click="loadRecords"
                v-bind="attrs"
                v-on="on"
                class="mr-1"
              >
                <v-icon>mdi-cached</v-icon>
              </v-btn>
            </template>
            <span>Refresh</span>
          </v-tooltip>

          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-tooltip top>
                <template
                  v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }"
                >
                  <v-btn
                    icon
                    color="secondary"
                    v-bind="{ ...attrs, ...tooltipAttrs }"
                    v-on="{ ...on, ...tooltipOn }"
                    :disabled="recordsLoading"
                  >
                    <v-icon>mdi-file-delimited-outline</v-icon>
                  </v-btn>
                </template>
                <span>Export to EXCEL</span>
              </v-tooltip>
            </template>

            <v-list dense>
              <v-list-item @click="exportCurrentPage">
                <v-list-item-icon>
                  <v-icon>mdi-table</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Export Current Page</v-list-item-title>
                  <v-list-item-subtitle
                    >{{ records.length }} records</v-list-item-subtitle
                  >
                </v-list-item-content>
              </v-list-item>

              <v-list-item @click="exportAllData" :disabled="exportingAll">
                <v-list-item-icon>
                  <v-icon v-if="!exportingAll">mdi-database</v-icon>
                  <v-progress-circular
                    v-else
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                  ></v-progress-circular>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Export All Data</v-list-item-title>
                  <v-list-item-subtitle v-if="!exportingAll"
                    >{{ pagination.total }} total records</v-list-item-subtitle
                  >
                  <v-list-item-subtitle v-else-if="fetchProgress.total > 0">
                    Fetching {{ fetchProgress.current }} of
                    {{ fetchProgress.total }} pages...
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-else
                    >Preparing export...</v-list-item-subtitle
                  >
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Hidden downloadexcel components for export functionality -->
          <downloadexcel
            ref="currentPageExport"
            :data="records"
            :fields="exportFields"
            :name="downloadExcelName('Current Page')"
            type="csv"
            style="display: none"
          />

          <downloadexcel
            ref="allDataExport"
            :data="allRecordsForExport"
            :fields="exportFields"
            :name="downloadExcelName('All Data')"
            type="csv"
            style="display: none"
          />
        </div>

        <div class="d-flex" style="align-items: center; gap: 1rem">
          <!-- Date Filter Dropdown -->
          <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                small
                v-bind="attrs"
                v-on="on"
                :disabled="recordsLoading"
                class="text-capitalize font-weight-regular px-2 grey--text text--darken-2"
              >
                {{ filter }}
                <v-icon small class="ps-1">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="item in filters"
                :key="item"
                @click="changeFilter(item)"
                :class="item == filter ? 'primary--text' : ''"
              >
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-text-field
            v-model="search"
            placeholder="Search by name, mobile..."
            single-line
            hide-details="auto"
            :disabled="recordsLoading"
            clearable
            outlined
            dense
            @input="handleSearch"
          >
          </v-text-field>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                :disabled="recordsLoading"
                @click="set_filter_dialog"
              >
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <span>Filter</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Active Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="d-flex align-center justify-space-between pa-3"
        style="background-color: #f5f5f5"
      >
        <div class="d-flex align-center flex-wrap" style="gap: 0.5rem">
          <span class="text-subtitle-2 grey--text mr-2">Active Filters:</span>

          <!-- Current Filter Display -->
          <v-chip
            small
            close
            color="primary"
            text-color="white"
            @click:close="clearDateFilters"
          >
            {{ filterDisplayText }}
          </v-chip>
        </div>

        <!-- Clear All Filters Button -->
        <v-btn small color="warning" @click="clearFilters" class="ml-2">
          <v-icon small class="mr-1">mdi-filter-remove</v-icon>
          Clear All
        </v-btn>
      </div>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="records"
          :loading="recordsLoading"
          loading-text="Loading, please wait a moment"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :server-items-length="pagination.total"
          :page.sync="currentPage"
          :items-per-page.sync="itemsPerPage"
          :footer-props="{
            'items-per-page-options': [5, 10, 25, 50, 100],
            'show-current-page': true,
            'show-first-last-page': true,
          }"
          @update:options="handlePaginationChange"
        >
          <template v-slot:[`item.player_id`]="{ item }">
            <span class="font-weight-medium">{{ item.player_id }}</span>
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <span class="font-weight-medium">{{ item.name }}</span>
          </template>

          <template v-slot:[`item.mobile_number`]="{ item }">
            <span>+63{{ item.mobile_number }}</span>
          </template>

          <template v-slot:[`item.account_verified`]="{ item }">
            <v-chip
              small
              :color="getVerificationColor(item.account_verified)"
              text-color="white"
            >
              {{ item.account_verified }}
            </v-chip>
          </template>

          <template v-slot:[`item.betting`]="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.betting)"
              text-color="white"
            >
              {{ item.betting }}
            </v-chip>
          </template>

          <template v-slot:[`item.cashin`]="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.cashin)"
              text-color="white"
            >
              {{ item.cashin }}
            </v-chip>
          </template>

          <template v-slot:[`item.cashout`]="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.cashout)"
              text-color="white"
            >
              {{ item.cashout }}
            </v-chip>
          </template>

          <template v-slot:[`item.is_active`]="{ item }">
            <v-chip
              small
              :color="getActiveStatusColor(item.is_active)"
              text-color="white"
            >
              {{ item.is_active }}
            </v-chip>
          </template>

          <template v-slot:[`item.registered_date`]="{ item }">
            <span>{{ formatDate(item.registered_date) }}</span>
          </template>

          <template v-slot:[`item.action`]="{ item }">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="primary"
                  @click="viewPlayerDetails(item.player_id)"
                >
                  <v-icon size="large"> mdi-text-box-search-outline</v-icon>
                </v-btn>
              </template>
              <span>View Details</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Filter Modal -->
    <NewPlayerFilter
      v-if="filterDialog"
      :data="filterData"
      @set_close_filter_dialog="set_close_filter_dialog"
    />

    <!-- Custom Date Picker Dialog -->
    <v-dialog
      ref="filterDateDialog"
      v-model="filterDateDialog"
      :return-value.sync="filterDate"
      persistent
      width="290px"
    >
      <v-date-picker v-model="filterDate" scrollable range>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="cancelDateFilter">Cancel</v-btn>
        <v-btn text color="primary" @click="saveDateFilter">OK</v-btn>
      </v-date-picker>
    </v-dialog>

    <!-- Export Progress Modal -->
    <v-dialog v-model="exportingAll" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-file-export</v-icon>
          Exporting Data
        </v-card-title>

        <v-card-text>
          <div class="text-center mb-4">
            <v-progress-circular
              :value="progressPercentage"
              :rotate="360"
              :size="100"
              :width="15"
              color="primary"
            >
              {{ Math.round(progressPercentage) }}%
            </v-progress-circular>
          </div>

          <div class="text-center">
            <p class="mb-2 font-weight-medium">
              {{ progressText }}
            </p>
            <p class="caption grey--text">
              Please wait while we fetch all records for export...
            </p>
          </div>

          <!-- Progress Details -->
          <v-progress-linear
            v-if="fetchProgress.total > 0"
            :value="progressPercentage"
            color="primary"
            height="6"
            rounded
            class="mt-3"
          ></v-progress-linear>

          <div
            v-if="fetchProgress.total > 0"
            class="d-flex justify-space-between caption mt-2"
          >
            <span
              >Page {{ fetchProgress.current }} of
              {{ fetchProgress.total }}</span
            >
            <span>{{ Math.round(progressPercentage) }}% Complete</span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import formatDatev2 from "@/helpers/formatDatev2";
import downloadexcel from "vue-json-excel";
import NewPlayerFilter from "@/components/players/NewPlayerFilter.vue";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";

export default {
  components: {
    NewPlayerFilter,
    downloadexcel,
  },
  data: () => ({
    search: "",
    searchTimeout: null,
    // Initialize with today's date as default
    today: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    dateFrom: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    dateTo: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    filter: "Today",
    filters: ["Today", "This Week", "This Month", "This Year", "Custom"],
    filterDialog: false,
    filterData: {},
    filterDateDialog: false,
    filterDate: [],
    filterDateText: "",
    sortBy: "player_id",
    sortDesc: true,
    currentPage: 1,
    itemsPerPage: 10,
    exportingAll: false,
    allRecordsForExport: [],
    fetchProgress: {
      current: 0,
      total: 0,
    },
    headers: [
      {
        text: "Action",
        sortable: false,
        value: "action",
        class: "text-uppercase",
        align: "center",
        width: "80px",
      },
      {
        text: "Player ID",
        value: "player_id",
        class: "text-uppercase",
        width: "100px",
      },
      {
        text: "Name",
        value: "name",
        class: "text-uppercase",
        width: "200px",
      },
      {
        text: "Mobile Number",
        value: "mobile_number",
        class: "text-uppercase",
        width: "150px",
      },
      {
        text: "Email",
        value: "email_address",
        class: "text-uppercase",
        width: "200px",
      },
      {
        text: "Account Verified",
        value: "account_verified",
        class: "text-uppercase",
        align: "center",
        width: "120px",
      },
      //   {
      //     text: "Betting",
      //     value: "betting",
      //     class: "text-uppercase",
      //     align: "center",
      //     width: "100px",
      //   },
      //   {
      //     text: "Cash In",
      //     value: "cashin",
      //     class: "text-uppercase",
      //     align: "center",
      //     width: "100px",
      //   },
      //   {
      //     text: "Cash Out",
      //     value: "cashout",
      //     class: "text-uppercase",
      //     align: "center",
      //     width: "100px",
      //   },
      {
        text: "Status",
        value: "is_active",
        class: "text-uppercase",
        align: "center",
        width: "100px",
      },
      {
        text: "Registered Date",
        value: "registered_date",
        class: "text-uppercase",
        width: "150px",
      },
      //   {
      //     text: "Agent",
      //     value: "agent",
      //     class: "text-uppercase",
      //     width: "150px",
      //   },
      {
        text: "City",
        value: "city_municipality",
        class: "text-uppercase",
        width: "150px",
      },
    ],
  }),
  created() {
    this.loadRecords();
  },
  computed: {
    ...mapState("newly_registered_players", [
      "records",
      "recordsLoading",
      "pagination",
    ]),
    hasActiveFilters() {
      return this.filter !== "Today" || this.search;
    },
    filterDisplayText() {
      if (this.filter === "Custom" && this.filterDateText) {
        return this.filterDateText;
      }
      return `${this.filter}: ${this.formatDisplayDate(this.dateFrom)}${
        this.dateFrom !== this.dateTo
          ? " to " + this.formatDisplayDate(this.dateTo)
          : ""
      }`;
    },
    exportFields() {
      return {
        "PLAYER ID": "player_id",
        NAME: "name",
        "FIRST NAME": "first_name",
        "MIDDLE NAME": "middle_name",
        "LAST NAME": "last_name",
        "MOBILE NUMBER": {
          callback: (value) => {
            return `+63${value.mobile_number}`;
          },
        },
        "EMAIL ADDRESS": "email_address",
        "ACCOUNT VERIFIED": "account_verified",
        // BETTING: "betting",
        // "CASH IN": "cashin",
        // "CASH OUT": "cashout",
        STATUS: "is_active",
        "REGISTERED DATE": {
          callback: (value) => {
            return this.formatDate(value.registered_date);
          },
        },
        // AGENT: "agent",
        BARANGAY: "barangay",
        CITY: "city_municipality",
        AGE: "age",
        GENDER: "gender",
        BIRTHDATE: "birthdate",
        NATIONALITY: "nationality",
        ADDRESS: "address",
        "MARITAL STATUS": "marital_status",
      };
    },
    progressPercentage() {
      if (this.fetchProgress.total === 0) return 0;
      return (this.fetchProgress.current / this.fetchProgress.total) * 100;
    },
    progressText() {
      if (this.fetchProgress.total === 0) {
        return "Preparing export...";
      }
      return `Fetching page ${this.fetchProgress.current} of ${this.fetchProgress.total}`;
    },
  },
  methods: {
    ...mapActions("newly_registered_players", ["getRecords"]),
    loadRecords() {
      if (!this.recordsLoading) {
        // Always ensure date_from and date_to are sent to API
        this.getRecords({
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
          search: this.search,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          date_from: this.dateFrom || this.today,
          date_to: this.dateTo || this.today,
        });
      }
    },
    changeFilter(val) {
      if (val === "Custom") {
        this.filterDateDialog = true;
      } else {
        switch (val) {
          case "Today":
            this.dateFrom = this.today;
            this.dateTo = this.today;
            this.filter = val;
            this.filterDate = [];
            break;

          case "This Week": {
            const startWeek = startOfWeek(new Date(this.today), {
              weekStartsOn: 1,
            });
            const endWeek = endOfWeek(new Date(this.today), {
              weekStartsOn: 1,
            });

            this.dateFrom = formatDatev2(startWeek, "format", "yyyy-MM-dd");
            this.dateTo = formatDatev2(endWeek, "format", "yyyy-MM-dd");
            this.filter = val;
            this.filterDate = [];
            break;
          }

          case "This Month": {
            const startMonth = startOfMonth(new Date(this.today));
            const endMonth = endOfMonth(new Date(this.today));

            this.dateFrom = formatDatev2(startMonth, "format", "yyyy-MM-dd");
            this.dateTo = formatDatev2(endMonth, "format", "yyyy-MM-dd");
            this.filter = val;
            this.filterDate = [];
            break;
          }

          case "This Year": {
            const startYear = startOfYear(new Date(this.today));
            const endYear = endOfYear(new Date(this.today));

            this.dateFrom = formatDatev2(startYear, "format", "yyyy-MM-dd");
            this.dateTo = formatDatev2(endYear, "format", "yyyy-MM-dd");
            this.filter = val;
            this.filterDate = [];
            break;
          }
        }

        this.currentPage = 1;
        this.loadRecords();
      }
    },
    cancelDateFilter() {
      this.filterDateDialog = false;
    },
    saveDateFilter() {
      if (this.filterDate.length) {
        this.$refs.filterDateDialog.save(this.filterDate);

        const minDate = new Date(this.filterDate[0]);

        if (typeof this.filterDate[1] !== "undefined") {
          const maxDate = new Date(this.filterDate[1]);

          if (minDate > maxDate) {
            this.dateFrom = formatDatev2(maxDate, "format", "yyyy-MM-dd");
            this.dateTo = formatDatev2(minDate, "format", "yyyy-MM-dd");
          } else {
            this.dateFrom = formatDatev2(minDate, "format", "yyyy-MM-dd");
            this.dateTo = formatDatev2(maxDate, "format", "yyyy-MM-dd");
          }

          this.filterDateText =
            formatDatev2(this.dateFrom, "date") +
            " to " +
            formatDatev2(this.dateTo, "date");
        } else {
          this.dateFrom = formatDatev2(minDate, "format", "yyyy-MM-dd");
          this.dateTo = formatDatev2(minDate, "format", "yyyy-MM-dd");
          this.filterDateText = formatDatev2(this.dateFrom, "date");
        }

        this.filter = "Custom";
        this.currentPage = 1;
        this.loadRecords();
      }
    },
    handleSearch() {
      // Debounce search input
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1; // Reset to first page when searching
        this.loadRecords();
      }, 500);
    },
    set_filter_dialog() {
      this.filterData = {
        date_from: this.dateFrom,
        date_to: this.dateTo,
      };
      this.filterDialog = !this.filterDialog;
    },
    set_close_filter_dialog(data) {
      if (data.type) {
        this.dateFrom = data.date_from || "";
        this.dateTo = data.date_to || "";
        this.currentPage = 1;
        this.loadRecords();
      }
      this.filterDialog = !this.filterDialog;
    },
    clearFilters() {
      this.search = "";
      this.dateFrom = this.today;
      this.dateTo = this.today;
      this.filter = "Today";
      this.filterDate = [];
      this.filterDateText = "";
      this.currentPage = 1;
      this.loadRecords();
    },
    clearDateFilters() {
      this.dateFrom = this.today;
      this.dateTo = this.today;
      this.filter = "Today";
      this.filterDate = [];
      this.filterDateText = "";
      this.currentPage = 1;
      this.loadRecords();
    },
    formatDisplayDate(date) {
      return formatDatev2(date);
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return formatDatev2(dateString, "datetime");
    },
    getVerificationColor(status) {
      switch (status) {
        case "Verified":
          return "success";
        case "Pending":
          return "warning";
        case "Rejected":
          return "error";
        default:
          return "grey";
      }
    },
    getStatusColor(status) {
      return status === "Enabled" ? "success" : "error";
    },
    getActiveStatusColor(status) {
      return status === "Active" ? "success" : "error";
    },
    downloadExcelName(type = "") {
      const suffix = type ? ` - ${type}` : "";
      return (
        "Newly Registered Players" +
        suffix +
        " " +
        formatDatev2(new Date()) +
        ".xls"
      );
    },
    exportCurrentPage() {
      this.$refs.currentPageExport.$el.click();
    },
    async exportAllData() {
      this.exportingAll = true;
      try {
        // Fetch all records using continuous pagination
        const allRecords = await this.getAllRecordsPaginated();
        this.allRecordsForExport = allRecords;

        // Wait for Vue to update the data
        await this.$nextTick();

        // Trigger the download
        this.$refs.allDataExport.$el.click();
      } catch (error) {
        console.error("Error exporting all data:", error);
        this.$store.commit(
          "setRefreshError",
          "Failed to export all data. Please try again.",
          { root: true }
        );
      } finally {
        this.exportingAll = false;
        this.allRecordsForExport = []; // Clear the data to free memory
        this.fetchProgress.current = 0; // Reset progress
        this.fetchProgress.total = 0;
      }
    },
    async getAllRecordsPaginated() {
      const allRecords = [];
      const pageSize = 5000; // Maximum safe page size
      let currentPage = 1;
      let totalPages = 1;

      // Reset progress
      this.fetchProgress.current = 0;
      this.fetchProgress.total = 0;

      const baseParams = {
        itemsPerPage: pageSize,
        search: this.search,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        date_from: this.dateFrom || this.today,
        date_to: this.dateTo || this.today,
      };

      do {
        const params = {
          ...baseParams,
          page: currentPage,
          returnData: true,
        };

        // Update progress before making the request
        this.fetchProgress.current = currentPage;
        if (currentPage === 1) {
          this.fetchProgress.total = 1;
        } else {
          this.fetchProgress.total = totalPages;
        }

        const response = await this.$store.dispatch(
          "newly_registered_players/getRecords",
          params
        );

        // Add records to our collection
        if (response && response.items) {
          allRecords.push(...response.items);

          // Update pagination info from first response
          if (currentPage === 1) {
            totalPages =
              response.totalPages || Math.ceil(response.total / pageSize);
            this.fetchProgress.total = totalPages;
          }
        }

        currentPage++;
      } while (currentPage <= totalPages);

      return allRecords;
    },
    handlePaginationChange(options) {
      const { page, itemsPerPage, sortBy, sortDesc } = options;

      this.currentPage = page;
      this.itemsPerPage = itemsPerPage;

      if (sortBy.length > 0) {
        this.sortBy = sortBy[0];
        this.sortDesc = sortDesc[0];
      }

      this.loadRecords();
    },
    viewPlayerDetails(playerId) {
      this.$router.push({ name: "PlayerDetails", params: { id: playerId } });
    },
  },
};
</script>
